import { ConfigAnswerDisplayTypes, Placeholder } from '@omq/types';
import { isMobileDevice, ServerError, useConfigContext } from '@omq/shared';

import { Search } from '../components/search/search';
import { QuestionNavigation } from '../components/navigation/question-navigation';
import { QuestionList } from '../components/question-list/question-list';
import { useCanonical } from '../hooks/canonical';
import { useDocumentTitle } from '../hooks/document-title';
import { HelpCategory, type HelpPage, HelpQuestion } from '../api/help';
import { ErrorView } from './error-view';
import { useState } from 'react';
import { useFaqStructuredData } from '../hooks/faq-structured-data';

/**
 * Type for component properties.
 */
type DetailViewProps = {
  currentHelpPage?: HelpPage;
  structuredDataIsEnabled?: boolean;
  question: HelpQuestion;
  onSearchChange: (value: string) => void;
  onRootClick: () => void;
  placeholder: Placeholder;
  categoryPath?: Array<HelpCategory>;
  onCategorySelection?: (category: string | null) => void;
};

/**
 * Detail view for question.
 *
 * Only shows a single question.
 * On mobile, as a modal; on larger screens as expanded item, which can't be closed.
 *
 * Also shows a navigation bar, with root item, and question title.
 *
 * @param {DetailViewProps} props - Component properties
 *
 * @author Florian Walch
 * @since 9.3
 *
 * @returns {JSX.Element}
 */
export function DetailView({
  currentHelpPage,
  structuredDataIsEnabled,
  question,
  onSearchChange,
  onRootClick,
  placeholder,
  categoryPath,
  onCategorySelection,
}: DetailViewProps): JSX.Element {
  const config = useConfigContext();
  const [currentError, setCurrentError] = useState<ServerError | null>(null);

  // add FAQ structured data for SEO
  useFaqStructuredData(currentHelpPage ?? null, structuredDataIsEnabled);

  // on mobile, it's important to leave detail view with
  // click handler, otherwise there is no chance to leave detail view
  // since there is no navigation bar
  const onQuestionClick = () => {
    isMobileDevice() || config.answerDisplayType === ConfigAnswerDisplayTypes.POPUP
      ? onRootClick()
      : null;
  };

  // add a canonical link for question (question is always set in detail view,
  // the category is passed for signature symmetry only)
  // categoryPath can be undefined at runtime when the page is not loaded (deep link to question)
  useCanonical(
    question,
    categoryPath?.[categoryPath.length - 1] ?? null,
    config.isCanonicalActive,
  );

  // update document.title for question
  useDocumentTitle(question.body, config.titlePattern);

  if (currentError != null) {
    return <ErrorView errorMessage={config.loc(`_error.${currentError.errorType}`)} />;
  }

  return (
    <div className={`${config.generateClassName('help')} notranslate`} translate="no">
      {/* set the title here for programmatic access to the tiele */}
      <span id={`${config.cssIdentifier}-help-title`} hidden>
        {question.body}
      </span>
      <div className={config.generateClassName('help-wrapper')}>
        <div className={config.generateClassName('help-container')}>
          <QuestionNavigation
            title={question.body}
            onRootClick={onRootClick}
            categories={categoryPath}
            onCategorySelection={onCategorySelection}
          />
        </div>

        <div className={config.generateClassName('help-container')}>
          <Search value="" autoCompleteItems={[]} onChange={onSearchChange} />
        </div>

        <div className={config.generateClassName('help-container')}>
          <div
            key={'question-list-element'}
            className={config.generateClassName('question-list')}
            aria-live="polite"
            aria-relevant={'additions'}>
            <QuestionList
              isInDetailView={true}
              questions={[question]}
              onQuestionClick={onQuestionClick}
              selectedQuestionId={question.id}
              placeholder={placeholder}
              onError={setCurrentError}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
